import { useEffect, useMemo, useState } from "react";
import { formatPhoneNumber } from "../../utils/phoneNumberUtils";

const getName = (person) =>
  person.name || `${person.firstName || ""} ${person.lastName || ""}`.trim() || "Unnamed";

const hasUsablePhone = (person) => Boolean(String(person.phone || "").trim());

const BulkSmsModal = ({ isOpen, onClose, recipients, onSend }) => {
  const [filter, setFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const sortedRecipients = useMemo(
    () => [...recipients].sort((a, b) => getName(a).localeCompare(getName(b))),
    [recipients],
  );

  useEffect(() => {
    if (!isOpen) return;
    setFilter("all");
    setMessage("");
    setError("");
    setSummary(null);
    setSelectedIds(
      new Set(
        sortedRecipients
          .filter((person) => hasUsablePhone(person) && !person.pauseNotifications)
          .map((person) => person.id),
      ),
    );
  }, [isOpen, sortedRecipients]);

  const filteredRecipients = useMemo(() => {
    if (filter === "drivers") return sortedRecipients.filter((p) => p.isDriver);
    if (filter === "participants") return sortedRecipients.filter((p) => !p.isDriver);
    if (filter === "valid") {
      return sortedRecipients.filter(
        (p) => hasUsablePhone(p) && !p.pauseNotifications,
      );
    }
    return sortedRecipients;
  }, [filter, sortedRecipients]);

  if (!isOpen) return null;

  const selectedCount = selectedIds.size;
  const selectedVisibleCount = filteredRecipients.filter((p) => selectedIds.has(p.id)).length;

  const toggleRecipient = (person) => {
    if (!hasUsablePhone(person) || person.pauseNotifications) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(person.id)) next.delete(person.id);
      else next.add(person.id);
      return next;
    });
  };

  const selectVisible = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      filteredRecipients.forEach((person) => {
        if (hasUsablePhone(person) && !person.pauseNotifications) next.add(person.id);
      });
      return next;
    });
  };

  const clearVisible = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      filteredRecipients.forEach((person) => next.delete(person.id));
      return next;
    });
  };

  const handleSend = async () => {
    setError("");
    setSummary(null);
    if (!message.trim()) {
      setError("Message is required.");
      return;
    }
    if (selectedIds.size === 0) {
      setError("Choose at least one recipient.");
      return;
    }

    setIsSending(true);
    const result = await onSend({
      message: message.trim(),
      participantIds: Array.from(selectedIds),
    });
    setIsSending(false);

    if (result?.ok === false) {
      setError(result.message || "Bulk SMS failed. Please try again.");
      return;
    }
    setSummary(result.summary || null);
  };

  return (
    <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-900 bg-opacity-70 px-3 py-6">
      <div className="mx-auto w-full max-w-5xl rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h4 className="text-xl font-black text-gray-900">Send Bulk SMS</h4>
            <p className="mt-1 text-sm text-gray-500">
              Select recipients, remove anyone you do not want to message, then send.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="select select-bordered select-sm w-full sm:w-48"
              >
                <option value="all">All members</option>
                <option value="participants">Participants only</option>
                <option value="drivers">Drivers only</option>
                <option value="valid">Can receive SMS</option>
              </select>
              <div className="flex gap-2">
                <button onClick={selectVisible} className="rounded-lg border px-3 py-2 text-xs font-bold">
                  Select visible
                </button>
                <button onClick={clearVisible} className="rounded-lg border px-3 py-2 text-xs font-bold">
                  Clear visible
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200">
              <div className="bg-gray-50 px-3 py-2 text-sm font-bold text-gray-700">
                {selectedCount} selected · {selectedVisibleCount} visible selected
              </div>
              <div className="max-h-96 overflow-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 bg-white text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Send</th>
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Phone</th>
                      <th className="px-3 py-2">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredRecipients.map((person) => {
                      const disabled = !hasUsablePhone(person) || person.pauseNotifications;
                      return (
                        <tr key={person.id} className={disabled ? "bg-gray-50 text-gray-400" : "bg-white"}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(person.id)}
                              disabled={disabled}
                              onChange={() => toggleRecipient(person)}
                              className="checkbox checkbox-sm"
                            />
                          </td>
                          <td className="px-3 py-2 font-semibold">
                            {getName(person)}
                            {person.pauseNotifications && (
                              <div className="text-xs font-normal text-orange-600">Notifications paused</div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {person.phone ? formatPhoneNumber(person.phone) : "Missing"}
                          </td>
                          <td className="px-3 py-2">{person.isDriver ? "Driver" : "Participant"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <label className="text-sm font-bold text-gray-700">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows="8"
              maxLength="1000"
              className="textarea textarea-bordered mt-2 w-full"
              placeholder="Type your group message..."
            />
            <p className="mt-1 text-xs text-gray-500">{message.length}/1000 characters</p>

            {error && <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
            {summary && (
              <div className="mt-3 rounded-lg bg-green-50 p-3 text-sm text-green-800">
                Sent {summary.sent}, skipped {summary.skipped}, failed {summary.failed}.
              </div>
            )}

            <button
              onClick={handleSend}
              disabled={isSending || selectedCount === 0 || !message.trim()}
              className="mt-4 w-full rounded-lg bg-[#004a7e] px-4 py-3 text-sm font-bold text-white hover:bg-[#003a64] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isSending ? "Sending..." : `Send to ${selectedCount} recipient${selectedCount === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkSmsModal;